import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Paper,
  Divider,
  useTheme,
  alpha,
  IconButton,
  DialogActions,
  Tooltip
} from "@mui/material";
import { ChangeEvent, useEffect, useState, useRef } from "react";
import IConversationSettings from "../types/interfaces/IConversationSettings";
import IModel from "../types/interfaces/IModel";
import getDefaultModelFromList from "../functions/comparisons-arrays/getDefaultModelFromList";
import sortLargeLanguageModels from "../functions/comparisons-arrays/sortLargeLanguageModels";
import { useOidcConfigurationName } from "../hooks/useOidcConfigurationName";
import { useOidcAccessToken, useOidcUser } from "@axa-fr/react-oidc";
import { useUserInfo } from "../hooks/useUserInfo";
import { useChatOptions } from "../hooks/useChatOptions";
import { useAvailableModels } from "../hooks/useAvailableModels";
import { useFormFactor } from "../hooks/useFormFactor";
import { FormFactor } from "../types/enums/FormFactor";
import RagSourceMultiSelect from "./Controls/RagSourceMultiSelect";
import { usePaginatedConversationList } from "../hooks/useConversationListQuery";
import { useParams } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import DnsIcon from '@mui/icons-material/Dns';
import InfoIcon from '@mui/icons-material/Info';

export type ConversationSettingsProps = {
  open: boolean;
  onClose: () => void;
  onSave: (newSettings: IConversationSettings) => void;
  currentModel: IModel;
  currentSystemPrompt: string;
  currentPii: boolean;
  currentRag: string[];
  defaultSystemPrompt: string;
};

export default function ConversationSettings({
  open,
  onClose,
  onSave,
  currentModel,
  currentSystemPrompt,
  currentPii,
  currentRag,
  defaultSystemPrompt,
}: ConversationSettingsProps) {
  const { availableModels } = useAvailableModels();
  const { oidcConfigurationName } = useOidcConfigurationName();
  const { oidcUser } = useOidcUser(oidcConfigurationName);
  const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
  const { roles } = useUserInfo(oidcUser, oidcAccessToken);
  const { chatOptions } = useChatOptions();
  const formFactor = useFormFactor();
  const [systemPrompt, setSystemPrompt] = useState<string>(currentSystemPrompt);
  const [model, setModel] = useState<IModel>(currentModel);
  const [rag, setRag] = useState<string[]>(currentRag);
  const [pii, setPii] = useState<boolean>(currentPii);
  const { id: activeConversationId } = useParams<{ id: string }>();
  const paginatedList = usePaginatedConversationList(50);
  const theme = useTheme();

  const showRag = chatOptions.show_rag;

  // Add a ref to track the first render
  const isFirstRender = useRef(true);

  const handleClose = () => {
    setPii(currentPii);
    setRag(currentRag);
    setModel(currentModel);
    setSystemPrompt(currentSystemPrompt);
    onClose();
  };

  useEffect(() => {
    // Only log on first render and then reset the ref
    if (isFirstRender.current) {
      console.log("ConversationSettings props changed:", {
        currentModelName: currentModel?.internal_name,
        systemPromptLength: currentSystemPrompt?.length
      });
      
      isFirstRender.current = false;
      
      // Reset after a short delay to capture actual changes but avoid loops
      setTimeout(() => {
        isFirstRender.current = true;
      }, 1000);
    }
    
    setModel(currentModel);
    setRag(currentRag);
    setPii(currentPii);
    setSystemPrompt(currentSystemPrompt);
    
    // Only log on first render
    if (isFirstRender.current) {
      console.log("Updated ConversationSettings state with model:", currentModel?.internal_name);
    }
  }, [currentSystemPrompt, currentPii, currentRag, currentModel]);

  const handleSave = () => {
    // Check if the model changed
    const modelChanged = model.internal_name !== currentModel.internal_name;
    if (modelChanged) {
      console.log("Model changed in settings:", {
        from: currentModel.internal_name,
        to: model.internal_name
      });
      
      // Show warning when switching to Perplexity models in an existing conversation
      if (model.internal_name.toLowerCase().includes("perplexity")) {
        // Check if this is an existing conversation with messages
        const activeThread = activeConversationId ? 
          paginatedList.threads?.find(t => t.id === activeConversationId) : null;
          
        // Only show warning if this conversation has messages
        if (activeThread && activeThread.updated_at) {
          const isConfirmed = window.confirm(
            "Switching to Perplexity models mid-conversation may cause errors due to message formatting requirements. " +
            "For best results, start a new conversation when using Perplexity models.\n\n" +
            "Do you want to continue with this change?"
          );
          
          if (!isConfirmed) {
            setModel(currentModel);
            return;
          }
        }
      }
    }
    
    onSave({
      systemPrompt: systemPrompt,
      model: model,
      pii: pii,
      rag: rag,
    });
    onClose();
  };

  const handleSystemPromptChange = (event: ChangeEvent<{ value: unknown }>) => {
    setSystemPrompt(event.target.value as string);
  };

  const handleModelChange = (event: SelectChangeEvent<{ value: unknown }>) => {
    setModel(
      availableModels.find((llm) => llm.internal_name === event.target.value) ??
        getDefaultModelFromList(availableModels),
    );
  };

  const handleRagChange = (newRag: string[]) => {
    setRag(newRag);
  };

  const handleResetSystemPrompt = () => {
    setSystemPrompt(defaultSystemPrompt);
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth={true}
      fullScreen={formFactor < FormFactor.Tablet}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: theme.palette.primary.main, 
        color: theme.palette.primary.contrastText,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 1
      }}>
        <Typography variant="h6" component="div">
          Conversation Settings
        </Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={handleClose} 
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3, 
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : alpha(theme.palette.background.paper, 0.7),
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <TextSnippetIcon 
              color="primary" 
              fontSize="small" 
              sx={{ mr: 1 }} 
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              System Prompt
            </Typography>
          </Box>
          
          <TextField
            multiline={true}
            minRows={4}
            maxRows={10}
            value={systemPrompt}
            placeholder="Enter a custom system prompt for this conversation"
            onChange={handleSystemPromptChange}
            variant="outlined"
            fullWidth
            sx={{ 
              mb: 1.5,
              '& .MuiInputBase-root': {
                bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined
              }
            }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="Reset to global default system prompt">
              <Button 
                onClick={handleResetSystemPrompt} 
                startIcon={<RestartAltIcon />}
                size="small"
                variant="outlined"
                color="primary"
              >
                Reset to Global Default
              </Button>
            </Tooltip>
          </Box>
        </Paper>
        
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3, 
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : alpha(theme.palette.background.paper, 0.7),
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <ModelTrainingIcon 
              color="primary" 
              fontSize="small" 
              sx={{ mr: 1 }} 
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              AI Model Selection
            </Typography>
          </Box>
          
          <FormControl fullWidth variant="outlined">
            <InputLabel id="modelSelectLabel">Model</InputLabel>
            <Select
              labelId="modelSelectLabel"
              // @ts-ignore
              value={model.internal_name}
              label="Model"
              onChange={handleModelChange}
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined
              }}
            >
              {availableModels
                .filter((m) => {
                  let x = Array.isArray(m.allowed_groups)
                    ? m.allowed_groups
                    : [m.allowed_groups];
                  return x.some((g) => roles?.includes(g) || g === "*");
                })
                .sort(sortLargeLanguageModels)
                .map((m) => (
                  <MenuItem key={m.internal_name} value={m.internal_name}>
                    {m.default ? (
                      <Typography component="span" fontWeight="bold">
                        {m.display_name + " (Default)"}
                      </Typography>
                    ) : (
                      m.display_name
                    )}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Paper>
        
        {showRag && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : alpha(theme.palette.background.paper, 0.7),
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <DnsIcon 
                color="primary" 
                fontSize="small" 
                sx={{ mr: 1 }} 
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Knowledge Sources (RAG)
              </Typography>
              <Tooltip title="Inform responses with available documents">
                <IconButton size="small" sx={{ ml: 0.5 }}>
                  <InfoIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <RagSourceMultiSelect
              onRagChange={handleRagChange}
              currentRagValue={rag}
            />
          </Paper>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        py: 2,
        backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : alpha(theme.palette.background.paper, 0.5),
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ 
            minWidth: '100px',
            borderRadius: 1.5
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          color="primary"
          sx={{ 
            minWidth: '100px',
            borderRadius: 1.5,
            ml: 2
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
